import * as preact from "preact";
import * as _ from "underscore";

import * as practiceUtils from "practiceUtils";
import { ChallengeProps } from "practiceUtils";
import * as languageMap from "shared/languageMap";
import stores from "stores/stores";
import MultipleChoiceButtonRow, {
  MultipleChoiceButtonRowPersistedState,
} from "views/MultipleChoiceButtonRow";
import PracticeContextView from "views/PracticeContextView";

interface State {
  distractors: string[];
  reportedRecallEase: boolean;
}

interface PersistedState {
  distractors: string[];
  reportedRecallEase: boolean;
  buttonRowState?: MultipleChoiceButtonRowPersistedState;
}

class ActiveMultipleChoiceChallengeView extends preact.Component<
  ChallengeProps,
  State
> {
  buttonRow: MultipleChoiceButtonRow | null = null;
  initialButtonRowState?: MultipleChoiceButtonRowPersistedState;

  constructor(props: ChallengeProps) {
    super(props);

    const persisted = props.persistedState as PersistedState | undefined;

    if (persisted) {
      this.state = {
        distractors: persisted.distractors,
        reportedRecallEase: persisted.reportedRecallEase,
      };
      this.initialButtonRowState = persisted.buttonRowState;
      return;
    }

    this.state = {
      distractors: _.sample(
        props.allUserWords
          .filter(
            (potentialDistractor) =>
              potentialDistractor.id !== props.userWord.id &&
              practiceUtils.normalize(potentialDistractor.state.value.word) !==
                practiceUtils.normalize(props.userWord.state.value.word) &&
              practiceUtils.normalize(
                potentialDistractor.state.value.translation
              ) !==
                practiceUtils.normalize(props.userWord.state.value.translation)
          )
          .map((userWord) => userWord.state.value.word),
        3
      ),
      reportedRecallEase: false,
    };
  }

  getPersistedState(): PersistedState {
    return {
      distractors: this.state.distractors,
      reportedRecallEase: this.state.reportedRecallEase,
      buttonRowState: this.buttonRow?.getPersistedState(),
    };
  }

  /**
   * Auto-Pronounce the word being tested, whichever option was picked - hearing
   * it after a wrong answer is the point at which it most helps encode.
   */
  pronounceWord() {
    if (!stores.user.speakingMode.value) {
      return;
    }
    practiceUtils.playTTS(
      this.props.userWord.state.value.language,
      this.props.userWord.state.value.word
    );
  }

  render() {
    const { userWord } = this.props;
    // Computed during render (not cached in state) so it reflects edits to the
    // word's context made mid-session, e.g. via the Edit Card modal.
    const clozeSentence = practiceUtils.getClozeSentence(
      userWord,
      this.props.selectedContextIndex
    );

    const alternativeTranslations =
      userWord.state.value.alternativeTranslations ?? [];

    return (
      <div>
        <h2>
          What means "{userWord.state.value.translation}"{" "}
          {alternativeTranslations.length > 0
            ? alternativeTranslations
                .map(
                  (alternativeTranslation) => `or "${alternativeTranslation}"`
                )
                .join(" ")
            : null}{" "}
          here?
        </h2>
        <p>
          <PracticeContextView
            clozeSentence={clozeSentence}
            rtl={
              languageMap.map[this.props.userWord.state.value.language]
                ?.direction === "RTL"
            }
            mode={
              this.state.reportedRecallEase
                ? {
                    type: "complete",
                    ttsLanguage: this.props.userWord.state.value.language,
                  }
                : { type: "blanked-out" }
            }
          />
        </p>
        <MultipleChoiceButtonRow
          ref={(ref) => (this.buttonRow = ref)}
          initialState={this.initialButtonRowState}
          correctAnswer={userWord.state.value.word}
          distractors={this.state.distractors}
          onSuccess={() => {
            this.props.completedCallback({
              type: "show-feedback",
              correct: "yes",
            });
            if (!this.state.reportedRecallEase) {
              this.props.reportRecallEase(5, 0.7);
              this.setState({ reportedRecallEase: true });
            }
            this.pronounceWord();
          }}
          onFail={() => {
            this.props.completedCallback({
              type: "show-feedback",
              correct: "no",
              message: {
                title: "Incorrect",
                body: `The correct answer is "${userWord.state.value.word}"`,
              },
            });
            if (!this.state.reportedRecallEase) {
              this.props.reportRecallEase(0, 0.7);
              this.setState({ reportedRecallEase: true });
            }
            this.pronounceWord();
          }}
          showCorrectAnswer={this.props.skipped}
          transliterationLanguage={userWord.state.value.language}
        />
      </div>
    );
  }
}

export default ActiveMultipleChoiceChallengeView;
